import React, { useState, useEffect, Fragment } from "react";
import { Menu, Transition } from "@headlessui/react";
import { FiChevronDown } from "react-icons/fi";
import classNames from "classnames";

/**
 * Filters Component
 *
 * Props:
 *  - options: string[]         --> The list of filter options.
 *  - maxTabs: number           --> The max number of visible buttons before overflowing into dropdown.
 *  - onSelect: (option) => void  --> Callback when an option is selected.
 *  - defaultOption: string     --> Which option is selected by default (falls back to first option).
 */
const Filters = ({ options, maxTabs, onSelect, defaultOption }) => {
  const [selected, setSelected] = useState(defaultOption || options[0]);
  const [isMobile, setIsMobile] = useState(false);

  // Determine mobile vs. desktop based on viewport width
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };
    handleResize(); // Check on mount
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  maxTabs = maxTabs || options.length;

  const visibleOptions = options.slice(0, maxTabs);
  const dropdownOptions = options.slice(maxTabs);

  const handleSelect = (option) => {
    setSelected(option);
    onSelect(option);
  };

  /* --------------------------------
   *  MOBILE VERSION: Single Dropdown
   * -------------------------------- */
  if (isMobile) {
    return (
      <div className="relative inline-block w-full text-left">
        <Menu as="div" className="w-full">
          <div>
            <Menu.Button
              className="flex items-center justify-between w-full rounded-md border border-neutral-dark bg-background px-4 py-2 text-sm font-medium text-text shadow-sm transition-colors
                         duration-200 hover:bg-neutral-light focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              <span>{selected}</span>
              <FiChevronDown className="w-4 h-4 ml-2" />
            </Menu.Button>
          </div>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Menu.Items className="absolute left-0 z-10 mt-2 w-full origin-top-left rounded-md bg-background shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
              {options.map((option, idx) => (
                <Menu.Item key={idx}>
                  {({ active }) => (
                    <button
                      onClick={() => handleSelect(option)}
                      className={classNames(
                        "block w-full px-4 py-2 text-left text-sm",
                        active && "bg-neutral-light",
                        selected === option
                          ? "bg-primary text-text-on-primary"
                          : "text-text"
                      )}
                    >
                      {option}
                    </button>
                  )}
                </Menu.Item>
              ))}
            </Menu.Items>
          </Transition>
        </Menu>
      </div>
    );
  }

  /* --------------------------------
   *  DESKTOP VERSION: Segmented Tabs
   * -------------------------------- */
  return (
    <div className="flex items-center space-x-2">
      <div className="inline-flex shadow-sm" role="group">
        {/* Visible tabs */}
        {visibleOptions.map((option, idx) => {
          const isSelected = selected === option;
          const isFirst = idx === 0;
          const isLast =
            idx === visibleOptions.length - 1 && dropdownOptions.length === 0;

          return (
            <button
              key={option}
              onClick={() => handleSelect(option)}
              className={classNames(
                "relative -ml-px first:ml-0 border border-neutral-dark px-4 py-2 text-sm font-medium focus:z-10",
                "transition-transform duration-150 ease-in-out hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
                isFirst && "rounded-l-md",
                isLast && "rounded-r-md",
                isSelected
                  ? "bg-primary text-text-on-primary border-primary"
                  : "bg-neutral-light text-text hover:border-primary"
              )}
            >
              {option}
            </button>
          );
        })}

        {/* Dropdown for overflowed tabs, if any */}
        {dropdownOptions.length > 0 && (
          <Menu as="div" className="relative -ml-px">
            <div>
              <Menu.Button
                className={classNames(
                  "inline-flex items-center justify-between border border-neutral-dark px-4 py-2 text-sm font-medium focus:z-10",
                  "transition-transform duration-150 ease-in-out hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
                  "rounded-r-md", // ensure right edge is rounded
                  dropdownOptions.includes(selected)
                    ? "bg-primary text-text-on-primary border-primary"
                    : "bg-neutral-light text-text hover:border-primary"
                )}
              >
                {/* If selected is in the dropdown list, show that; otherwise "More" */}
                {dropdownOptions.includes(selected) ? selected : "More"}
                <FiChevronDown className="w-4 h-4 ml-2" />
              </Menu.Button>
            </div>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Menu.Items className="absolute left-0 z-10 mt-2 w-44 origin-top-left rounded-md bg-background shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                {dropdownOptions.map((option) => (
                  <Menu.Item key={option}>
                    {({ active }) => (
                      <button
                        onClick={() => handleSelect(option)}
                        className={classNames(
                          "block w-full px-4 py-2 text-left text-sm transition-colors",
                          active && "bg-neutral-light",
                          selected === option
                            ? "bg-primary text-text-on-primary"
                            : "text-text"
                        )}
                      >
                        {option}
                      </button>
                    )}
                  </Menu.Item>
                ))}
              </Menu.Items>
            </Transition>
          </Menu>
        )}
      </div>
    </div>
  );
};

export default Filters;
